import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const statements = [
  {
    title: 'The founding hire defines the centre.',
    body: "Before the team, before the engineering culture, before the employer brand — there is one decision that sets the trajectory. The founding leader is not just a hire. They are the organisation's first signal to the market about who you are.",
  },
  {
    title: 'GCC success is a talent problem, solved upstream.',
    body: 'The GCCs that scale fastest have leaders who hire well from day one. We work with you before the entity is formed, shaping the talent strategy that determines everything downstream.',
  },
  {
    title: 'Transactional search is the wrong frame.',
    body: 'Most firms fill a role. We interrogate it. Why this role now? What outcome are you actually trying to achieve? Who has genuinely done this — not a version of it, but exactly this?',
  },
]

export default function PositioningSection() {
  const [open, setOpen] = useState(0)

  return (
    <section
      data-testid="positioning-section"
      id="about"
      className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left: sticky heading */}
          <div>
            <div className="lg:sticky lg:top-32">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              >
                <p className="text-[10px] tracking-[0.35em] uppercase text-[#0A0A0A]/30 mb-8 font-body font-semibold">
                  Our Perspective
                </p>
                <h2 className="text-[clamp(2rem,4vw,4.5rem)] font-heading font-black tracking-tighter leading-[1.0] text-[#0A0A0A] mb-8">
                  From the founding leader<br />
                  <span className="font-light text-[#16365F]">to the teams that make<br />the centre succeed.</span>
                </h2>
                <div className="w-10 h-px bg-[#0A0A0A]" />
              </motion.div>
            </div>
          </div>

          {/* Right: interactive accordion */}
          <div className="space-y-0">
            {statements.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
                className="border-t border-[#E5E7EB]"
                data-testid={`positioning-statement-${i}`}
              >
                <button
                  className="w-full text-left py-8 flex items-start justify-between gap-4 group"
                  onClick={() => setOpen(open === i ? -1 : i)}
                  aria-expanded={open === i}
                >
                  <div className="flex items-start gap-5">
                    <p className="text-[9px] tracking-[0.3em] uppercase text-[#0A0A0A]/20 font-body font-semibold pt-1.5 flex-shrink-0">
                      {String(i + 1).padStart(2, '0')}
                    </p>
                    <h3 className={`text-lg md:text-xl font-heading font-bold leading-tight tracking-tight transition-colors duration-200 ${open === i ? 'text-[#0B1F3A]' : 'text-[#0A0A0A]'}`}>
                      {s.title}
                    </h3>
                  </div>
                  <span className={`text-[#0A0A0A]/30 text-lg leading-none transition-transform duration-300 flex-shrink-0 mt-1 ${open === i ? 'rotate-45' : ''}`}>
                    +
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="text-sm font-body text-[#0A0A0A]/55 leading-relaxed pb-8 pl-10">
                        {s.body}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
